import React from 'react'

import PropTypes from 'prop-types'

import './QuestionContent.css'
import { useTranslation } from 'react-i18next'
import {
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormCheck,
  CFormLabel,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilX } from '@coreui/icons'
import { FileUpload } from 'src/services/upload'
import { FileUploadModules } from 'src/services/upload/types'
import Editor from 'src/components/editor/Editor'
import { Question } from './types'
import PaginatedMultiSel from 'src/components/paginatedmultisel/PaginatedMultiSel'
import { getFields } from 'src/services/api'

const QuestionContent = ({ question, position, onDelete, canDelete }) => {
  const { t } = useTranslation()
  const fileUploader = new FileUpload()

  const handleDelete = () => {
    if (onDelete) onDelete(position)
  }

  const handleUploadTextImage = (image, progress) => {
    return fileUploader.send(image, FileUploadModules.QUESTIONDB, progress)
  }

  return (
    <CAccordionItem itemKey={question.key}>
      <CAccordionHeader>
        {t('Question {{position}}', { position: position.toString().padStart(2, '0') })}
      </CAccordionHeader>
      <CAccordionBody>
        <PaginatedMultiSel
          fetchItems={getFields}
          defaultItems={question.field.value ? [{ ...question.field.value, selected: true }] : null}
          onChange={(values) => {
            question.field.value = values && values.length === 1 ? values[0] : null
          }}
          multiple={false}
          label={t('Field')}
        />
        <CCard className="mt-4 mb-4">
          <CCardHeader>{t('Statement')}</CCardHeader>
          <CCardBody className="m-0 p-0">
            <Editor
              key={`question-editor-${question.key}`}
              id={`question-editor-${question.key}`}
              onUpload={handleUploadTextImage}
              onChange={(value) => (question.statement.value = value)}
              value={question.statement.value}
            />
          </CCardBody>
        </CCard>
        {question.options.map((option, index) => (
          <CCard key={index} className="mt-2">
            <CCardHeader>
              {t('Option {{letter}}', { letter: String.fromCharCode(65 + index) })}
            </CCardHeader>
            <CCardBody className="m-0 p-0">
              <Editor
                key={`question-${question.key}-option-${index}`}
                id={`question-${question.key}-option-${index}`}
                onUpload={handleUploadTextImage}
                onChange={(value) => (option.value = value)}
                value={option.value}
                height={100}
              />
            </CCardBody>
          </CCard>
        ))}

        <CRow className="mt-3">
          <CCol xs={12}>
            <CFormLabel>{t('Answer')}</CFormLabel>
          </CCol>
          <CCol xs={12}>
            <CRow>
              <CButtonGroup
                role="group"
                onChange={(event) => {
                  for (let idx = 0; idx < question.options.length; idx++) {
                    question.options[idx].isCorrect = idx === event.target.value
                  }
                }}
              >
                {question.options.map((option, index) => (
                  <CFormCheck
                    key={`question-${question.key}-option-${index}-check`}
                    type="radio"
                    defaultChecked={option.isCorrect}
                    button={{ color: 'success', variant: 'outline' }}
                    name={`question-${question.key}-check`}
                    id={`question-${question.key}-option-${index}-check`}
                    value={index}
                    autoComplete="off"
                    label={String.fromCharCode(65 + index)}
                  />
                ))}
              </CButtonGroup>
            </CRow>
          </CCol>
        </CRow>

        <CRow className="mt-3">
          <CCol xs={12} className="justify-content-end d-flex">
            <CButton onClick={() => handleDelete()} disabled={!canDelete} color="danger" key="1">
              <CIcon icon={cilX} className="me-2" />
              {t('Delete')}
            </CButton>
          </CCol>
        </CRow>
      </CAccordionBody>
    </CAccordionItem>
  )
}

QuestionContent.propTypes = {
  question: PropTypes.objectOf(Question),
  position: PropTypes.number,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
}

export default React.memo(QuestionContent)
